import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ArrowLeft,
    Copy,
    Eye,
    EyeOff,
    RefreshCw,
    AlertCircle,
    ChevronDown,
    ChevronRight,
    Server,
    Workflow,
    CheckCircle2,
    Loader2,
    Plug,
} from 'lucide-react';
import { SiteHeader } from '@/components/site-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import {
    getProjectMcpServer,
    updateProjectMcpStatus,
    rotateProjectMcpToken,
    getProjectMcpHttpUrl,
} from '@/api/mcp';
import { getMcpProjectId } from '@/api/lib/client';
import type { ProjectMcpServer, ProjectMcpFlow } from '@/api/mcp/mcp.types';

//  Skeletons 

const FlowRowSkeleton = () => (
    <div className="flex items-center justify-between py-3 px-4">
        <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-md" />
            <div className="space-y-1.5">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-20" />
            </div>
        </div>
        <Skeleton className="h-5 w-14 rounded-full" />
    </div>
);

const ConnectionSkeleton = () => (
    <div className="space-y-5">
        <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full" />
        </div>
        <Skeleton className="h-10 w-full rounded-lg" />
    </div>
);

//  Helpers 

function getFlowDisplayName(flow: ProjectMcpFlow): string {
    return flow.version?.displayName || flow.latest_version_displayName || flow.id;
}

function getFlowToolInfo(flow: ProjectMcpFlow): { toolName: string; toolDescription: string } | null {
    const input =
        flow.version?.trigger?.settings?.input ||
        flow.latest_version_trigger?.settings?.input;
    if (!input?.toolName) return null;
    return { toolName: input.toolName, toolDescription: input.toolDescription || '' };
}

//  Main Component 

export function McpServerDashboard() {
    const navigate = useNavigate();
    const enhancedToast = useEnhancedToast();

    const [isLoading, setIsLoading] = useState(true);
    const [projectId, setProjectId] = useState<string | null>(null);
    const [mcpServer, setMcpServer] = useState<ProjectMcpServer | null>(null);

    const [isTogglingEnabled, setIsTogglingEnabled] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const [isRotatingToken, setIsRotatingToken] = useState(false);
    const [copiedUrl, setCopiedUrl] = useState(false);
    const [copiedToken, setCopiedToken] = useState(false);
    const [showJsonConfig, setShowJsonConfig] = useState(false);
    const [copiedJson, setCopiedJson] = useState(false);

    const isEnabled = mcpServer?.status === 'ENABLED';
    const serverUrl = projectId ? getProjectMcpHttpUrl(projectId) : '';

    //  Load 

    const loadMcpServer = useCallback(async () => {
        setIsLoading(true);
        try {
            const pid = await getMcpProjectId();
            setProjectId(pid);

            if (!pid) {
                enhancedToast.error({
                    icon: AlertCircle,
                    title: 'Authentication error',
                    description: 'Could not resolve project ID.',
                });
                return;
            }

            const response = await getProjectMcpServer();
            if (response.error) {
                enhancedToast.error({
                    icon: AlertCircle,
                    title: 'Failed to load MCP server',
                    description: response.error,
                });
            } else {
                setMcpServer(response.data ?? null);
            }
        } finally {
            setIsLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadMcpServer();
    }, [loadMcpServer]);

    //  Enable / Disable 

    const handleToggleEnabled = async (enabled: boolean) => {
        setIsTogglingEnabled(true);
        try {
            const response = await updateProjectMcpStatus(enabled ? 'ENABLED' : 'DISABLED');
            if (response.error) {
                enhancedToast.error({
                    icon: AlertCircle,
                    title: `Failed to ${enabled ? 'enable' : 'disable'} MCP access`,
                    description: response.error,
                });
            } else {
                setMcpServer(response.data ?? null);
                enhancedToast.success({
                    icon: CheckCircle2,
                    title: enabled ? 'MCP access enabled' : 'MCP access disabled',
                    description: enabled
                        ? 'External agents can now access your project flows.'
                        : 'MCP access has been disabled.',
                });
            }
        } finally {
            setIsTogglingEnabled(false);
        }
    };

    //  Rotate Token 

    const handleRotateToken = async () => {
        setIsRotatingToken(true);
        try {
            const response = await rotateProjectMcpToken();
            if (response.error) {
                enhancedToast.error({
                    icon: AlertCircle,
                    title: 'Failed to rotate token',
                    description: response.error,
                });
            } else {
                setMcpServer(response.data ?? null);
                setShowToken(false);
                enhancedToast.success({
                    icon: CheckCircle2,
                    title: 'Token rotated',
                    description: 'Your MCP token has been regenerated. Update any clients using the old token.',
                });
            }
        } finally {
            setIsRotatingToken(false);
        }
    };

    //  Copy helpers 

    const copyToClipboard = async (text: string, type: 'url' | 'token' | 'json') => {
        try {
            await navigator.clipboard.writeText(text);
            if (type === 'url') { setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 2000); }
            else if (type === 'token') { setCopiedToken(true); setTimeout(() => setCopiedToken(false), 2000); }
            else { setCopiedJson(true); setTimeout(() => setCopiedJson(false), 2000); }
        } catch {
            enhancedToast.error({ icon: AlertCircle, title: 'Copy failed', description: 'Could not copy to clipboard.' });
        }
    };

    //  JSON config 

    const jsonConfig = mcpServer
        ? JSON.stringify(
              {
                  mcpServers: {
                      activepieces: {
                          url: serverUrl,
                          headers: { Authorization: `Bearer ${mcpServer.token}` },
                      },
                  },
              },
              null,
              2
          )
        : '';

    //  Render 

    return (
        <>
            <SiteHeader
                title="MCP Server"
                subtitle="Allow external agents to read and trigger your project's flows"
                backButton={
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/dashboard/workflows')}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>
                }
            />

            <div className="flex-1 overflow-auto">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-4 py-6 md:px-8 md:py-8 space-y-6"
                >
                    {/*  Enable / Disable card (full width)  */}
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between gap-4">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <Server className="h-4 w-4 text-primary" />
                                        <span className="font-semibold">Enable MCP Access</span>
                                        {!isLoading && (
                                            <Badge
                                                variant={isEnabled ? 'default' : 'secondary'}
                                                className="text-[10px] px-1.5 py-0"
                                            >
                                                {isEnabled ? 'Enabled' : 'Disabled'}
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        Allow external agents to read and trigger your project's flows securely.
                                    </p>
                                </div>
                                {isLoading ? (
                                    <Skeleton className="h-6 w-11 rounded-full" />
                                ) : (
                                    <Switch
                                        checked={isEnabled}
                                        onCheckedChange={handleToggleEnabled}
                                        disabled={isTogglingEnabled}
                                    />
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/*  Two-column layout  */}
                    {(isLoading || isEnabled) && (
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">

                            {/* Left: Connection Details (3/5 width) */}
                            <Card className="lg:col-span-3">
                                <CardHeader className="pb-4">
                                    <CardTitle className="text-base">Connection Details</CardTitle>
                                    <CardDescription>
                                        Use these details to connect external MCP clients to your project.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-5">
                                    {isLoading ? (
                                        <ConnectionSkeleton />
                                    ) : (
                                        <>
                                            {/* Server URL */}
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Server URL</label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        readOnly
                                                        value={serverUrl}
                                                        className="flex-1 h-10 rounded-md border border-input bg-muted/50 px-3 text-sm font-mono text-muted-foreground focus:outline-none min-w-0"
                                                    />
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-10 w-10 flex-shrink-0"
                                                        onClick={() => copyToClipboard(serverUrl, 'url')}
                                                        title="Copy URL"
                                                    >
                                                        {copiedUrl ? (
                                                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                                                        ) : (
                                                            <Copy className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Token */}
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Token</label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        readOnly
                                                        type={showToken ? 'text' : 'password'}
                                                        value={mcpServer?.token ?? ''}
                                                        className="flex-1 h-10 rounded-md border border-input bg-muted/50 px-3 text-sm font-mono text-muted-foreground focus:outline-none min-w-0"
                                                    />
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-10 w-10 flex-shrink-0"
                                                        onClick={() => setShowToken(v => !v)}
                                                        title={showToken ? 'Hide token' : 'Show token'}
                                                    >
                                                        {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-10 w-10 flex-shrink-0"
                                                        onClick={handleRotateToken}
                                                        disabled={isRotatingToken}
                                                        title="Rotate token"
                                                    >
                                                        {isRotatingToken ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <RefreshCw className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-10 w-10 flex-shrink-0"
                                                        onClick={() => copyToClipboard(mcpServer?.token ?? '', 'token')}
                                                        title="Copy token"
                                                    >
                                                        {copiedToken ? (
                                                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                                                        ) : (
                                                            <Copy className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                </div>
                                                <p className="text-xs text-muted-foreground">
                                                    Use this token with the{' '}
                                                    <code className="px-1 py-0.5 rounded bg-muted font-mono text-xs">
                                                        Authorization
                                                    </code>{' '}
                                                    header (Bearer) for requests to this server.
                                                </p>
                                            </div>

                                            {/* MCP Client Configuration JSON */}
                                            <div className="border border-border rounded-lg overflow-hidden">
                                                <button
                                                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/50 transition-colors text-left"
                                                    onClick={() => setShowJsonConfig(v => !v)}
                                                >
                                                    <span>MCP Client Configuration (JSON)</span>
                                                    {showJsonConfig ? (
                                                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                    ) : (
                                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                    )}
                                                </button>
                                                {showJsonConfig && (
                                                    <div className="border-t border-border bg-muted/30">
                                                        <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                                                            <span className="text-xs text-muted-foreground">
                                                                Add to your MCP client configuration file
                                                            </span>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-7 px-2 text-xs gap-1"
                                                                onClick={() => copyToClipboard(jsonConfig, 'json')}
                                                            >
                                                                {copiedJson ? (
                                                                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                                                                ) : (
                                                                    <Copy className="h-3.5 w-3.5" />
                                                                )}
                                                                {copiedJson ? 'Copied' : 'Copy'}
                                                            </Button>
                                                        </div>
                                                        <pre className="p-4 text-xs font-mono text-muted-foreground overflow-x-auto whitespace-pre leading-relaxed">
                                                            {jsonConfig}
                                                        </pre>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Right: Available Flows (2/5 width) */}
                            <Card className="lg:col-span-2">
                                <CardHeader className="pb-4">
                                    <CardTitle className="text-base">Available Flows</CardTitle>
                                    <CardDescription>
                                        Flows with an MCP Trigger will appear here and be accessible via your MCP server.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="p-0">
                                    {isLoading ? (
                                        <div className="divide-y divide-border px-1">
                                            <FlowRowSkeleton />
                                            <FlowRowSkeleton />
                                            <FlowRowSkeleton />
                                        </div>
                                    ) : !mcpServer?.flows?.length ? (
                                        <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
                                            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                                                <Plug className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                            <p className="text-sm font-medium mb-1">No flows connected</p>
                                            <p className="text-xs text-muted-foreground max-w-[200px]">
                                                Add an MCP Trigger to a flow in your workspace to expose it here.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-border">
                                            {mcpServer.flows.map(flow => {
                                                const displayName = getFlowDisplayName(flow);
                                                const toolInfo = getFlowToolInfo(flow);
                                                const isFlowEnabled = flow.status === 'ENABLED';

                                                return (
                                                    <div
                                                        key={flow.id}
                                                        className="flex items-center justify-between gap-3 px-4 py-3"
                                                    >
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                                <Workflow className="h-4 w-4 text-primary" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-medium truncate">
                                                                    {displayName}
                                                                </p>
                                                                {toolInfo && (
                                                                    <p className="text-xs text-muted-foreground truncate">
                                                                        {toolInfo.toolName}
                                                                        {toolInfo.toolDescription && toolInfo.toolDescription !== toolInfo.toolName && (
                                                                            <>  {toolInfo.toolDescription}</>
                                                                        )}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span
                                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                                                                isFlowEnabled
                                                                    ? 'bg-green-500/15 text-green-600 dark:text-green-400'
                                                                    : 'bg-muted text-muted-foreground'
                                                            }`}
                                                        >
                                                            <span
                                                                className={`h-1.5 w-1.5 rounded-full ${
                                                                    isFlowEnabled
                                                                        ? 'bg-green-500'
                                                                        : 'bg-muted-foreground/50'
                                                                }`}
                                                            />
                                                            {isFlowEnabled ? 'On' : 'Off'}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                        </div>
                    )}

                    {/*  Disabled state hint  */}
                    {!isLoading && !isEnabled && (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-4">
                                <Server className="h-6 w-6 text-muted-foreground" />
                            </div>
                            <p className="text-sm font-medium mb-1">MCP access is disabled</p>
                            <p className="text-xs text-muted-foreground max-w-xs">
                                Toggle the switch above to enable MCP access and expose your flows to external agents.
                            </p>
                        </div>
                    )}
                </motion.div>
            </div>
        </>
    );
}
